import dayjs from 'dayjs';
import type { Facility, FacilityCertificate, Supplier } from 'features/suppliers/types';
import {
  facilitiesDataCache,
  getOrgSuppliers,
  type MockFacility,
} from 'mocks/api/handlers/facilitiesCache';
import { getSuppliersData } from 'mocks/api/handlers/suppliersCache';

/**
 * Deterministic, additive seed for the certificate five-state status lifecycle e2e epic
 * (DL-65097 / Testmo folder `Certificate Status Lifecycle`, group_id 208186).
 *
 * One facility, five certificates — one per status value the badge can render
 * (`not_yet_valid` / `valid` / `expiring_soon` / `expired`) plus the `inactive` precedence case.
 * Distinct sub-supplier/facility/certificate ids (`certsl` / `csl*`) from every other
 * facility-certificate epic (`certfixture`/`cf*` — dl-63961, `certcrud`/`cc*` — dl-63719,
 * `certlazy`/`cl*` — DL-64870) so none of them ever contend for the same MirageJS cache entry
 * under Playwright's parallel workers.
 *
 * Dates are computed relative to `dayjs()` at mock-boot time (never literal calendar dates — the
 * epic's own ground rule) and kept well clear of the Valid/Expiring-Soon notification-date
 * boundary and the Expired/Not-yet-valid ValidFrom/ValidTo boundaries, per
 * `CERTIFICATE_DEFAULT_EXPIRATION_NOTIFICATION_DAYS_BEFORE_VALID_TO` (90 days,
 * `features/supplier/utils/certificateDateValidation.ts`).
 *
 * Applied only under `VITE_MOCK_ONLY` (see `mocks/api/index.ts`), after the other
 * facility-certificate fixtures, so it only ADDS a sub-supplier and its one facility — it never
 * replaces or mutates the existing `certfixture` / `certcrud` / `certlazy` seeds.
 */

/** Supplier org id the supplier-portal session resolves to (see `mocks/api/handlers/auth.ts`). */
export const STATUS_LIFECYCLE_SUPPLIER_ORG_ID = 63;

/** Stable ids used across list -> detail -> facilities -> certificates and in `data-testid`s. */
export const STATUS_LIFECYCLE_FIXTURE = {
  subSupplierId: 'certsl',
  subSupplierName: 'Certificate Status Lifecycle Subsupplier',
  facilityId: 'cslfacility',
  certs: {
    /** TC-SL-001 / DL-65100 — ValidFrom > today. */
    notYetValid: 'csl-not-yet-valid',
    /** TC-SL-002 / DL-65101 — today between ValidFrom and the notification date. */
    valid: 'csl-valid',
    /** TC-SL-003 / DL-65098 — today between the notification date and ValidTo (the spine). */
    expiringSoon: 'csl-expiring-soon',
    /** TC-SL-004 / DL-65102 — today > ValidTo. */
    expired: 'csl-expired',
    /** TC-SL-005 / DL-65099 — manually inactivated; dates match `expired` so the precedence
     *  rule (date-computed label wins over the raw `inactive` status) is unambiguous. */
    inactive: 'csl-inactive',
  },
} as const;

const today = () => dayjs().startOf('day');
/** ISO date `offsetDays` away from today (negative = past), well clear of any boundary. */
const isoDaysFromToday = (offsetDays: number) => today().add(offsetDays, 'day').toISOString();

const DATES = {
  // ValidFrom well in the future -> Not yet valid.
  notYetValid: { validFrom: isoDaysFromToday(30), validTo: isoDaysFromToday(500) },
  // Notification date (validTo - 90) is still in the future -> Valid.
  valid: { validFrom: isoDaysFromToday(-100), validTo: isoDaysFromToday(300) },
  // Notification date (validTo - 90) is in the past, ValidTo is still ahead -> Expiring soon.
  expiringSoon: { validFrom: isoDaysFromToday(-300), validTo: isoDaysFromToday(30) },
  // ValidTo well in the past -> Expired.
  expired: { validFrom: isoDaysFromToday(-600), validTo: isoDaysFromToday(-100) },
} as const;

let standardIdSeq = 990_100;
let instituteIdSeq = 990_500;

const makeCert = (
  id: string,
  standardName: string,
  status: string,
  dates: { validFrom: string; validTo: string }
): FacilityCertificate => ({
  id,
  certificateNumber: `CERT-${id.toUpperCase()}`,
  standard: {
    id: standardIdSeq++,
    name: standardName,
    certificateType: 'facility',
  },
  institute: {
    id: instituteIdSeq++,
    name: 'Status Lifecycle Fixture Institute',
  },
  // The declared `FacilityCertificateStatus` union predates the real API status enum
  // (`not_yet_valid` / `expiring_soon` are missing from it, same as `certlazy`'s fixture) — cast
  // the same way the existing certificate mock handlers do (`facilityCertificates.ts`).
  status: status as FacilityCertificate['status'],
  validFrom: dates.validFrom,
  validTo: dates.validTo,
});

const buildFacility = (): MockFacility => {
  const c = STATUS_LIFECYCLE_FIXTURE.certs;
  const certificates: FacilityCertificate[] = [
    makeCert(c.notYetValid, 'GOTS', 'not_yet_valid', DATES.notYetValid),
    makeCert(c.valid, 'GRS', 'valid', DATES.valid),
    makeCert(c.expiringSoon, 'OEKO-TEX Standard 100', 'expiring_soon', DATES.expiringSoon),
    makeCert(c.expired, 'BCI', 'expired', DATES.expired),
    // Inactive precedence case (TC-SL-005): same dates as the `expired` row, but the seeded API
    // `status` is `inactive`. `getExpiryStatusAndColor` falls back to a date computation for
    // `inactive` (`features/supplier/utils/certificateStatus.ts`), so the badge must still read
    // "Expired" — never "Inactive" (no such label exists) and never the raw `inactive` value.
    makeCert(c.inactive, 'RCS', 'inactive', DATES.expired),
  ];

  const base: Facility = {
    id: STATUS_LIFECYCLE_FIXTURE.facilityId,
    organizationId: STATUS_LIFECYCLE_SUPPLIER_ORG_ID,
    organizationSubSupplierId: STATUS_LIFECYCLE_FIXTURE.subSupplierId,
    name: 'Status Lifecycle Facility',
    address: '5 Status Lifecycle Way',
    zipCode: '4000',
    city: 'Odense',
    stateOrRegion: 'Southern Denmark',
    countryCode: 'DK',
    latitude: 55.4038,
    longitude: 10.4024,
    website: 'https://example.com',
    vatNumber: 'DK33333333',
    mid: 'MID-STATUSLIFECYCLE',
    relationType: 1,
    processes: [],
    isArchived: false,
    isInUse: true,
    certificateCount: certificates.length,
  };
  return { ...base, facilityCertificates: certificates };
};

const makeSupplier = (id: string, name: string, organizationId: number): Supplier => ({
  id,
  organizationId,
  name,
  countryCode: 'DK',
  city: 'Odense',
  address: '5 Status Lifecycle Way',
  relationType: 1,
  avatarColor: '#8a4a9e',
  archived: false,
  isArchived: false,
  isSupplierInUse: true,
  contacts: [],
  facilities: [],
});

let seeded = false;

/**
 * Seed the deterministic certificate-status-lifecycle fixture. Idempotent and additive: prepends
 * one sub-supplier to the supplier-portal list (org 63) and fills its otherwise-unused
 * facility-cache key with the single scenario facility above. Registered in BOTH the sub-suppliers
 * list cache (reachable from My Subsuppliers -> Facilities) and the org-suppliers cache (so it
 * appears in the org-wide All Facilities aggregate) — mirrors `facilityCertificatesLazyLoadFixture`'s
 * dual registration.
 */
export const seedCertificateStatusLifecycleFixture = (): void => {
  if (seeded) return;
  seeded = true;

  const supplier = makeSupplier(
    STATUS_LIFECYCLE_FIXTURE.subSupplierId,
    STATUS_LIFECYCLE_FIXTURE.subSupplierName,
    STATUS_LIFECYCLE_SUPPLIER_ORG_ID
  );

  const subSuppliers = getSuppliersData(STATUS_LIFECYCLE_SUPPLIER_ORG_ID);
  if (!subSuppliers.some(s => s.id === STATUS_LIFECYCLE_FIXTURE.subSupplierId)) {
    subSuppliers.unshift(supplier);
  }

  const orgSuppliers = getOrgSuppliers(STATUS_LIFECYCLE_SUPPLIER_ORG_ID);
  if (!orgSuppliers.some(s => s.id === STATUS_LIFECYCLE_FIXTURE.subSupplierId)) {
    orgSuppliers.unshift(supplier);
  }

  facilitiesDataCache.set(STATUS_LIFECYCLE_FIXTURE.subSupplierId, [buildFacility()]);
};
